import { ArrowUpRight, Check, CircleDot, Gauge, Network, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { caseStudies } from "@/data/enhancementData";
import { images, manifesto, partners, services } from "@/data/siteData";

const HomePage = () => (
  <>
    <section className="home-hero" data-testid="home-hero-section">
      <div className="container hero-layout">
        <div className="hero-copy">
          <div className="hero-eyebrow"><span className="live-dot" /> DUBAI / BUSINESS TECHNOLOGY SYSTEMS</div>
          <h1 className="hero-title" data-testid="home-hero-title">
            <div className="hero-line-mask"><span className="hero-line hero-line-one">Empowering Businesses</span></div>
            <div className="hero-line-mask"><span className="hero-line hero-line-two">Through Technology</span></div>
            <div className="hero-line-mask"><span className="hero-line hero-line-three">Excellence</span></div>
          </h1>
          <p className="hero-lead">Hardware, print, networking and software solutions designed around the way your business needs to move.</p>
          <div className="hero-actions">
            <Link to="/contact" className="button button-cyan" data-testid="home-hero-primary-cta">Start a conversation <ArrowUpRight size={17} /></Link>
            <Link to="/services" className="button button-ghost-light" data-testid="home-hero-secondary-cta">Explore capabilities <ArrowUpRight size={17} /></Link>
          </div>
          <div className="hero-meta">
            <span>DXB // 25.2591° N, 55.2934° E</span>
            <span>VORTEX / 001</span>
          </div>
        </div>
        <div className="hero-visual" data-testid="home-hero-visual">
          <div className="hero-visual-frame">
            <img src={images.hero} alt="Cyan-lit enterprise network hardware" />
            <div className="hero-image-wash" />
            <div className="hero-index">01 <span>/</span> 05</div>
            <div className="hero-signal-card" data-testid="home-signal-card">
              <span className="signal-label"><CircleDot size={12} /> LIVE SYSTEM SIGNAL</span>
              <strong>READY FOR THE NEXT REQUEST</strong>
              <span className="signal-caption">Networks, devices and people in one clear view.</span>
            </div>
          </div>
          <div className="hero-tech-card">
            <span className="tech-card-label">CORE / STATUS</span>
            <div className="tech-card-value"><Gauge size={17} /> 99.99</div>
            <span className="tech-card-caption">operational confidence</span>
          </div>
        </div>
      </div>
    </section>

    <section className="marquee-band" aria-label="Technology standards and partners">
      <div className="marquee-label">FIELD NOTES / <span>01</span></div>
      <div className="marquee-viewport">
        <div className="marquee-track">
          {[...Array(2)].flatMap((_, repeat) => ["Hardware that fits", "Networks with intent", "Print without friction", "Software with clarity", "Dubai based", "Built for momentum"].map((item, index) => <span key={`${repeat}-${index}`}>{item} <b>＋</b></span>))}
        </div>
      </div>
    </section>

    <section className="section stats-section">
      <Reveal className="container">
        <div className="stats-grid" data-testid="home-stats-grid">
          <div className="stat-cell"><strong>07</strong><span>solution families</span></div>
          <div className="stat-cell"><strong>08</strong><span>industries in view</span></div>
          <div className="stat-cell"><strong>01</strong><span>clear local partner</span></div>
          <div className="stat-cell"><strong>∞</strong><span>room to scale</span></div>
        </div>
      </Reveal>
    </section>

    <section className="section manifesto-section">
      <div className="container">
        <Reveal className="section-heading section-heading-wide">
          <div><span className="eyebrow"><span className="eyebrow-rule" />A better operating rhythm</span><h2>Technology should feel like momentum.</h2></div>
          <p>VORTEX CORE makes the complex legible — then builds the dependable layer that lets your people focus on the work ahead.</p>
        </Reveal>
        <div className="manifesto-grid">
          {manifesto.map((item, index) => <Reveal key={item.number} delay={index * 80}><article className="manifesto-card" data-testid={`home-manifesto-${item.number}`}><span className="chapter-number">{item.number}</span><h3>{item.title}</h3><p>{item.text}</p><span className="chapter-line" /></article></Reveal>)}
        </div>
      </div>
    </section>

    <section className="section home-services-section">
      <div className="container">
        <Reveal className="section-heading">
          <div><span className="eyebrow"><span className="eyebrow-rule" />What we make possible</span><h2>Seven ways to keep the business moving.</h2></div>
          <Link className="text-link" to="/services" data-testid="home-services-link">View all services <ArrowUpRight size={16} /></Link>
        </Reveal>
        <div className="service-preview-list">
          {services.map((service, index) => { const Icon = service.Icon; return <Reveal key={service.id} delay={index * 45}><Link to={`/services#service-${service.id}`} className="service-preview-row" data-testid={`home-service-${service.id}`}><span className="service-row-number">{service.number}</span><span className="service-row-icon"><Icon size={20} /></span><span className="service-row-copy"><strong>{service.title}</strong><small>{service.short}</small></span><ArrowUpRight className="service-row-arrow" size={20} /></Link></Reveal>; })}
        </div>
      </div>
    </section>

    <section className="section proof-section">
      <div className="container proof-layout">
        <Reveal className="proof-image-wrap"><img src={images.network} alt="Precision fiber optic network infrastructure" /><span className="image-corner-tag">INFRASTRUCTURE / DETAIL 02</span></Reveal>
        <Reveal className="proof-copy" delay={120}>
          <span className="eyebrow"><span className="eyebrow-rule" />Why VORTEX CORE</span>
          <h2>The right technology is a business decision.</h2>
          <p className="lead-copy">We bring the practical eye of a local partner and the standards of an enterprise team to every recommendation.</p>
          <div className="proof-points"><div><ShieldCheck size={20} /><span><strong>Specific, not generic</strong><small>Every solution starts with the context behind the request.</small></span></div><div><Network size={20} /><span><strong>Connected by design</strong><small>Devices, print, networks and software work better as one system.</small></span></div><div><Check size={20} /><span><strong>Clear at handover</strong><small>Your team should know what is installed and why it matters.</small></span></div></div>
        </Reveal>
      </div>
    </section>

    <section className="section case-studies-section">
      <div className="container">
        <Reveal className="section-heading"><div><span className="eyebrow"><span className="eyebrow-rule" />In the field / capability snapshots</span><h2>Three ways the brief becomes useful.</h2></div><p>Anonymized, short-form snapshots of the kinds of environments VORTEX CORE helps make clearer.</p></Reveal>
        <div className="case-study-grid">
          {caseStudies.map((study, index) => <Reveal key={study.id} delay={index * 80}><article className="case-study-card" data-testid={`case-study-${study.id}`}><div className="case-study-top"><span>{study.number} / {study.sector}</span><span>ANONYMIZED</span></div><h3>{study.title}</h3><p>{study.body}</p><div className="case-study-focus"><span>Focus</span><strong>{study.focus}</strong></div></article></Reveal>)}
        </div>
      </div>
    </section>

    <section className="partner-band" data-testid="home-partner-strip">
      <div className="container partner-strip"><span className="partner-label">Working across the stack</span><div className="partner-list">{partners.map((partner) => <span key={partner} data-testid={`partner-logo-${partner.toLowerCase().replace(/\s/g, "-")}`}>{partner}</span>)}</div></div>
    </section>

    <section className="section home-cta-section">
      <Reveal className="container"><div className="cta-panel"><span className="eyebrow eyebrow-light"><span className="eyebrow-rule" />The next move</span><h2>Bring us the system you need to make simpler.</h2><p>Tell us what is slowing the business down. We will start with the useful questions.</p><Link to="/contact" className="button button-cyan" data-testid="home-bottom-cta">Open the conversation <ArrowUpRight size={17} /></Link><span className="cta-coordinate">DXB / VORTEX CORE / 2026</span></div></Reveal>
    </section>
  </>
);

export default HomePage;
